import { GoogleAuthProvider, OAuthProvider } from 'firebase/auth';
import { getMicrosoftAuthTenant } from './authConfig';

export const AUTH_ERROR_PROVIDER_NOT_CONFIGURED = 'auth/provider-not-configured';

export function getLinkedProviderIds(user) {
  return Array.from(new Set(
    (user?.providerData || [])
      .map((provider) => provider?.providerId)
      .filter(Boolean)
  ));
}

export function createGoogleAuthProvider() {
  return new GoogleAuthProvider();
}

export function createMicrosoftAuthProvider(authTenant = getMicrosoftAuthTenant()) {
  const normalizedAuthTenant = String(authTenant || '').trim();
  if (!normalizedAuthTenant) {
    const error = new Error('Microsoft sign-in is not configured.');
    error.code = AUTH_ERROR_PROVIDER_NOT_CONFIGURED;
    throw error;
  }

  const provider = new OAuthProvider('microsoft.com');
  provider.setCustomParameters({ tenant: normalizedAuthTenant });
  return provider;
}

const AUTH_ERROR_MESSAGES = Object.freeze({
  [AUTH_ERROR_PROVIDER_NOT_CONFIGURED]: 'Microsoft sign-in is not configured for this environment. Please contact an administrator.',
  'auth/admin-restricted-operation': 'Sign-in is restricted to invited accounts. Please contact an administrator to request access.',
  'auth/popup-closed-by-user': 'Sign-in was cancelled.',
  'auth/popup-blocked': 'Sign-in popup was blocked by your browser. Please allow popups for this site and try again.',
  'auth/account-exists-with-different-credential': 'An account already exists with this email address. Please sign in using your existing method, then connect this provider from Settings.',
  'auth/credential-already-in-use': 'This sign-in account is already linked to another Alphamizer user.',
  'auth/provider-already-linked': 'This sign-in method is already connected to your account.',
  'auth/email-already-in-use': 'That email address is already connected to another account.',
  'auth/invalid-action-code': 'This secure email link is invalid or has already been used. Ask an administrator to resend the invitation.',
  'auth/expired-action-code': 'This secure email link has expired. Ask an administrator to resend the invitation.',
  'auth/invalid-email': 'Enter the same valid email address that received the invitation.',
  'auth/requires-recent-login': 'This operation requires recent authentication. Please sign out and sign in again before proceeding.',
  'auth/weak-password': 'Choose a stronger password with at least six characters.',
  'auth/user-mismatch': 'Your authentication session changed. Please sign in again and retry.',
  'auth/user-disabled': 'Your account is pending admin approval or has been disabled. Please contact an administrator.',
  'auth/operation-not-allowed': 'This sign-in provider is not enabled. Please contact an administrator.',
  'auth/unauthorized-domain': 'This domain is not configured for authentication. Please contact an administrator.',
});

export function describeAuthError(error) {
  return AUTH_ERROR_MESSAGES[error?.code]
    || 'Unable to complete authentication. Please try again.';
}
